<section
  [id]="'comment-' + comment().id"
  class="m-sm"
  [class.mt-lg]="!isFirst()"
  [class.border-b]="!isLast() && hasLength()"
  [class.border-neutral-200]="!isLast() && hasLength()">
  <header class="mb-md">
    <div class="flex items-center justify-between">
      <!-- user details -->
      <div class="flex items-center gap-sm">
        <lib-avatar
          [img]="comment().user.image?.url ?? ''"
          [size]="''"
          [fullName]="comment().user.name"
          [imgDimensions]="{ width: 2, height: 2 }"
          borderRadius="rounded-full"
          unit="rem" />

        <h6 class="font-normal text-neutral-900 text-base">
          {{ comment().user.name }}
        </h6>
      </div>

      <!-- time and actions -->
      <div class="flex items-center gap-sm">
        <p class="font-medium text-neutral-700 text-xs">
          <span dir="ltr">
            {{ comment().created_at | date: 'dd-MMM-yyyy hh:mm a' }}
          </span>

          @if (comment().updated_at !== comment().created_at) {
            . {{ 'discussion_section_edited_label' | transloco }}
          }
        </p>

        @if (user().id === comment().user.id && !showAttachmentsOnly()) {
          <lib-action-list
            [menuItems]="actionMenuItems()"
            (itemClick)="handleAction($event)"
            [iconClass]="'size-6'" />
        }
      </div>
    </div>

    <p class="ms-5xl font-medium text-neutral-700 text-xs2">
      {{ comment().position_name }}
    </p>
  </header>

  <div class="ms-5xl">
    <!-- comment content -->
    <div
      class="flex flex-col gap-md border-neutral-200"
      [class.border-b]="!showAttachmentsOnly()">
      <div class="font-normal text-neutral-900 text-sm pb-md">
        <!-- hide content if attachments only is true -->
        @if (!showAttachmentsOnly()) {
          @let needToTruncate =
            comment().content &&
            comment().content!.length > defaultTruncateLength;
          <div
            class="rich-text-content break-words"
            [class.truncate-single-line]="needToTruncate && isTruncated()"
            [innerHTML]="comment().content ?? '' | sanitizeHtml"></div>
          @if (needToTruncate && isTruncated()) {
            <button
              type="button"
              class="text-primary-500 text-sm"
              (click)="isTruncated.set(false)">
              {{ 'discussion_section_see_more_button' | transloco }}
            </button>
          } @else {
            @if (needToTruncate) {
              <button
                type="button"
                class="text-primary-500 text-sm"
                (click)="isTruncated.set(true)">
                {{ 'discussion_section_see_less_button' | transloco }}
              </button>
            }
          }
        }

        @if (comment().attachments.length) {
          <div
            class="pt-md"
            [class.w-fit]="!showAttachmentsOnly()"
            [class.w-full]="showAttachmentsOnly()">
            <lib-view-file
              [label]="''"
              [type]="showAttachmentsOnly() ? 'list' : 'icon'"
              [uploadedFiles]="comment().attachments || []"
              [uploadFileLabels]="{
                attachmentsTitle: 'upload_files_attachments_title' | transloco,
                singleFileUploadedLabel:
                  'upload_files_single_file_uploaded_label' | transloco,
                multipleFilesUploadedLabel:
                  'upload_files_attachments_title' | transloco,
              }" />
          </div>
        }
      </div>

      <!-- reaction list should not be shown if attachments only is true -->
      @let reactions = comment().reactions ?? [];
      @if (reactions.length && !showAttachmentsOnly()) {
        <lib-comment-reaction-list
          [commentReactions]="comment().reactions ?? []"
          [reactionCount]="comment().reactions_count ?? 0"
          [allCommentReactions]="commentReactions()"
          [showAllReactions]="showAllReactions()"
          [emojisLookup]="reactionList()"
          [meta]="commentReactionsMeta()"
          [isReactionsLoading]="isReactionsLoading()"
          (toggleEmojiTab)="
            toggleEmojiTab.emit({ comment: comment(), emojiSlug: $event })
          "
          (toggleUserReactions)="
            toggleUserReactions.emit({
              showAllReactions: $event,
              comment: comment(),
            })
          " />
      }
    </div>

    <!-- hide react button if attachments only is true -->
    @if (!showAttachmentsOnly()) {
      <!-- react button -->
      <div
        class="inline-block relative"
        (mouseenter)="showReactionList.set(true)"
        (mouseleave)="showReactionList.set(false)">
        <button
          type="button"
          class="flex items-center gap-sm my-md p-sm rounded-sm"
          [class.bg-primary-50]="comment().user_reaction || react()"
          (click)="addEmoji(reactionList()[0])">
          @if (!comment().user_reaction && !react()) {
            <lib-like-icon
              iconClass="size-5"
              stroke="var(--color-neutral-500)" />
          } @else {
            <span class="font-medium text-neutral-500 text-sm">
              {{ comment().user_reaction?.emoji?.emoji || react()?.emoji }}
            </span>
          }

          <span
            class="font-medium text-sm"
            [class.text-primary-500]="comment().user_reaction || react()"
            [class.text-neutral-500]="!comment().user_reaction && !react()">
            @if (!comment().user_reaction && !react()) {
              {{ 'discussion_section_like_button' | transloco }}
            } @else {
              {{ comment().user_reaction?.emoji?.name || react()?.label }}
            }
          </span>
        </button>

        @if (showReactionList()) {
          <div
            class="absolute bottom-full start-1/2 ltr:-translate-x-1/3 rtl:translate-x-1/3 z-10 min-w-[10rem] bg-white rounded-lg p-sm reaction-list shadow-md">
            <div class="flex items-center justify-center gap-sm">
              @for (reaction of reactionList(); track $index) {
                <button
                  type="button"
                  class="text-neutral-500 text-base"
                  (click)="addEmoji(reaction)">
                  {{ reaction.emoji }}
                </button>
              }
            </div>
          </div>
        }
      </div>
    }
  </div>
</section>

<!-- Delete Message Modal -->
<ng-template #deleteCommentModal>
  <lib-delete-confirmation-popup
    [deleteItemLabels]="{
      deleteItemPopupTitle: 'delete_message_modal_title',
      deleteItemPopupDescription: 'delete_message_modal_description',
      deleteItemPopupYesButton: 'delete_message_modal_delete_message_button',
      deleteItemPopupNoButton: 'delete_message_modal_no_keep_it_button',
    }"
    [isDeleteItemLoading]="isDeleteCommentLoading()"
    (deleteItem)="confirmDelete()"
    (closeModal)="closeModal()" />
</ng-template>
